/**
 * ERP operations catalog — scope kinds, operation types, and system operation ids.
 * Keep in lockstep with erp_api/routes/controllers/operationsCatalog.ts.
 */

import { RecordKind, recordScopeGroupField, ASSET_CONDITION_OPTIONS } from './recordCatalog';

export const OPERATION_SCOPE_KINDS = [
  'organization',
  'location',
  'department',
  'designation',
  'team',
] as const;

export type OperationScopeKind = typeof OPERATION_SCOPE_KINDS[number];

/** User-facing operation class names (maps 1:1 to non-org scope kinds). */
export const OPERATION_CLASS_LABELS: Record<Exclude<OperationScopeKind, 'organization'>, string> = {
  location: 'Location',
  department: 'Department',
  designation: 'Designation',
  team: 'Teams',
};

export const OPERATION_TYPES = [
  'immediate_action',
  'fixed',
  'trigger',
  'recurring',
  'fixed_recurring',
] as const;

export type OperationType = typeof OPERATION_TYPES[number];

export const OPERATION_TYPE_LABELS: Record<OperationType, string> = {
  immediate_action: 'Immediate action',
  fixed: 'Fixed',
  trigger: 'Trigger',
  recurring: 'Recurring',
  fixed_recurring: 'Fixed recurring',
};

/** Operation types that dispatch work-order emails on save (like immediate). */
export const OPERATION_TYPES_DISPATCH_ON_SAVE: OperationType[] = [
  'immediate_action',
  'fixed',
];

/** Operation types with a scheduler interval. */
export const OPERATION_TYPES_SCHEDULED: OperationType[] = [
  'recurring',
  'fixed_recurring',
];

/** Parse HH:MM (24h) into minutes from midnight; null when invalid. */
export const parseOperationFixedWindowTime = (raw: unknown): number | null => {
  const text = String(raw || '').trim();
  const match = text.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return (hours * 60) + minutes;
};

/** Format minutes from midnight as HH:MM for operation data. */
export const formatOperationFixedWindowTime = (minutes: number): string => {
  const safe = Math.max(0, Math.min(23 * 60 + 59, Math.floor(minutes)));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** True when local time sits inside the operation's fixed-behavior daily window. */
export const operationIsInFixedBehaviorWindow = (
  op: OperationLifecycleProbe,
  now = new Date(),
): boolean => {
  const data = op.data || {};
  const start = parseOperationFixedWindowTime(data.fixedWindowStart);
  const end = parseOperationFixedWindowTime(data.fixedWindowEnd);
  if (start == null || end == null) return true;
  const nowMins = (now.getHours() * 60) + now.getMinutes();
  if (start <= end) return nowMins >= start && nowMins < end;
  return nowMins >= start || nowMins < end;
};

/** Fixed types keep the assigned form open after submit; fixed_recurring only inside its window. */
export const operationKeepsFormOpenAfterSubmit = (
  op: OperationLifecycleProbe,
  now = new Date(),
): boolean => {
  if (op.operationType === 'fixed') return true;
  if (op.operationType === 'fixed_recurring') {
    return operationIsInFixedBehaviorWindow(op, now);
  }
  return false;
};

export const OPERATION_NAV_GROUP_LABELS = [
  'Directive',
  'All operations',
] as const;

export type OperationNavGroupLabel = typeof OPERATION_NAV_GROUP_LABELS[number];

/** Sidebar selection id for the flat all-operations list (not an erp_records row). */
export const ALL_OPERATIONS_NAV_SCOPE_ID = '__all__';

export const isAllOperationsNavGroup = (group: OperationNavGroupLabel): boolean => (
  group === 'All operations'
);

/** Mandates + Manager tiers inside Location/Department/Designation/Teams switchers. */
export const OPERATION_STRUCTURAL_WORK_ORDER_GROUPS = [
  'Mandates',
  'ManagerDirectives',
] as const;

export const OPERATION_EXPIRY_MODES = ['indefinite', 'date'] as const;
export type OperationExpiryMode = typeof OPERATION_EXPIRY_MODES[number];

/** Undeletable system invite triggers — one per invite work order. */
export const SYSTEM_INVITE_EMAIL_OPERATION_ID = '-op-invite-email';
export const SYSTEM_INVITE_EMAIL_OPERATION_NAME = 'Send employee invite email';
export const SYSTEM_SUPPLIER_INVITE_EMAIL_OPERATION_ID = '-op-invite-email-supplier';
export const SYSTEM_SUPPLIER_INVITE_EMAIL_OPERATION_NAME = 'Send supplier invite email';

export const SYSTEM_INVITE_OPERATION_IDS = [
  SYSTEM_INVITE_EMAIL_OPERATION_ID,
  SYSTEM_SUPPLIER_INVITE_EMAIL_OPERATION_ID,
] as const;

/** Organization directive — work order + operation auto-synced when erp_directives saves. */
export const DIRECTIVE_WORK_ORDER_ID = '-form-org-directive';
export const DIRECTIVE_OPERATION_ID = '-op-org-directive';

/** Tenant organizations.id — legacy system ops may use this as scope_id. */
export const readTenantOrganizationId = (): string => (
  typeof localStorage !== 'undefined'
    ? String(localStorage.getItem('organization_data_id') || '').trim()
    : ''
);

/** erp_records organization id plus legacy tenant id for matching org-scoped operations. */
export const organizationOperationScopeIds = (
  records: Array<{ id?: string, kind?: string }>,
): Set<string> => {
  const ids = new Set<string>();
  const org = records.find((row) => row.kind === 'organization');
  if (org?.id) ids.add(String(org.id));
  const tenantId = readTenantOrganizationId();
  if (tenantId) ids.add(tenantId);
  return ids;
};

export const isOrganizationScopedOperation = (
  op: { scopeKind?: string | null },
): boolean => String(op.scopeKind || '') === 'organization';

export const operationMatchesOrganizationScope = (
  op: { scopeKind?: string | null, scopeId?: string | null },
  scopeIds: Set<string>,
): boolean => (
  isOrganizationScopedOperation(op)
  && scopeIds.has(String(op.scopeId || '').trim())
);

export const OPERATION_TRIGGER_KEYS = [
  'employee_invite_created',
  'employee_invite_resent',
  'supplier_invite_created',
  'supplier_invite_resent',
] as const;

export type OperationTriggerKey = typeof OPERATION_TRIGGER_KEYS[number];

export const OPERATION_ACTION_KEYS = [
  'send_invite_email',
  'send_work_order_email',
] as const;
export type OperationActionKey = typeof OPERATION_ACTION_KEYS[number];

/** Required objects an operation declares — supplies values for each.
 * `email` is reserved for system invite work orders (not user-selectable).
 * Legacy rows may still store objects on the linked work order until copied onto the operation. */
/** Retired work-order objects — stripped on parse (legacy rows). */
export const REMOVED_WORK_ORDER_OBJECT_TYPES = [
  'structural_groups',
  'management_group',
] as const;

export const WORK_ORDER_OBJECT_TYPES = [
  'email',
  'designation',
  'location',
  'department',
  'team',
  'employee',
  'general_assign',
] as const;
export type WorkOrderObjectType = typeof WORK_ORDER_OBJECT_TYPES[number];

/** Objects the work-order editor may pick (invite emails stay system-only). */
export const WORK_ORDER_OBJECT_USER_TYPES = [
  'designation',
  'location',
  'department',
  'team',
  'employee',
  'general_assign',
] as const;
export type WorkOrderObjectUserType = typeof WORK_ORDER_OBJECT_USER_TYPES[number];

export const WORK_ORDER_OBJECT_LABELS: Record<WorkOrderObjectType, string> = {
  email: 'Email object',
  designation: 'Designation object',
  location: 'Location object',
  department: 'Department object',
  team: 'Team object',
  employee: 'Employee object',
  general_assign: 'General assign object',
};

/** Head scopes offered under Structural Groups (same set as Structure master data). */
export const WORK_ORDER_STRUCTURAL_HEAD_SCOPES = [
  'organization',
  'location',
  'department',
  'designation',
  'employee',
  'management',
  'team',
] as const;
export type WorkOrderStructuralHeadScope = typeof WORK_ORDER_STRUCTURAL_HEAD_SCOPES[number];

export const WORK_ORDER_STRUCTURAL_HEAD_SCOPE_LABELS: Record<WorkOrderStructuralHeadScope, string> = {
  organization: 'Organization',
  location: 'Location',
  department: 'Department',
  designation: 'Designation',
  employee: 'Employee',
  management: 'Management',
  team: 'Team',
};

/** Head scopes: legacy built-ins plus any custom type code stored on the work order. */
export const isWorkOrderStructuralHeadScope = (raw: unknown): raw is WorkOrderStructuralHeadScope => (
  (WORK_ORDER_STRUCTURAL_HEAD_SCOPES as readonly string[]).includes(String(raw || '').trim())
);

/**
 * Accepts legacy built-in head scopes OR a custom type code (raw custom head-scope data).
 * Empty string means unset.
 */
export const parseWorkOrderStructuralHeadScope = (raw: unknown): string => {
  const value = String(raw || '').trim();
  if (!value) return '';
  // Any non-empty stored value is kept — dropdowns supply custom type codes or legacy kinds.
  return value.slice(0, 80);
};

/** Email work-order object is tracked in activity logs as email (track_email). */
export const WORK_ORDER_EMAIL_TRACKING_ITEM = 'track_email' as const;
/** Designation work-order object is tracked as a head-scope link. */
export const WORK_ORDER_DESIGNATION_TRACKING_ITEM = 'track_head_scope' as const;
/** Location work-order object is tracked as a head-scope link. */
export const WORK_ORDER_LOCATION_TRACKING_ITEM = 'track_head_scope' as const;
/** Department work-order object is tracked as a head-scope link. */
export const WORK_ORDER_DEPARTMENT_TRACKING_ITEM = 'track_head_scope' as const;
/** Structural Groups work-order object is tracked as a head-scope link. */
export const WORK_ORDER_STRUCTURAL_GROUPS_TRACKING_ITEM = 'track_head_scope' as const;
/** Management object work-order object is tracked as a head-scope link. */
export const WORK_ORDER_MANAGEMENT_GROUP_TRACKING_ITEM = 'track_head_scope' as const;
/** Team work-order object is tracked as a head-scope link. */
export const WORK_ORDER_TEAM_TRACKING_ITEM = 'track_head_scope' as const;

/** Work order flavor — basic ops, assets mobility, sale/expense/direct-entry transactions, POS sales, or document. */
export const WORK_ORDER_KINDS = [
  'basic', 'assets_mobility', 'document', 'directive', 'sale', 'expense', 'direct_entry', 'pos_sales',
] as const;
export type WorkOrderKind = typeof WORK_ORDER_KINDS[number];

export const WORK_ORDER_KIND_LABELS: Record<WorkOrderKind, string> = {
  basic: 'Basic',
  assets_mobility: 'Assets mobility',
  document: 'Document',
  directive: 'Directive',
  sale: 'Sale',
  expense: 'Expense',
  direct_entry: 'Direct entry',
  pos_sales: 'POS sales',
};

/** Hidden yes/no tracker set to Yes when the recipient acknowledges a Document work order. */
export const DOCUMENT_WORK_ORDER_ACKNOWLEDGE_KEY = 'acknowledge';

/** Direct entry — optional linked catalog rows + per-item prices (data mode only). */
export const WORK_ORDER_DIRECT_ENTRY_PRICE_FIELD = 'workOrderDirectEntryPrice';
export const WORK_ORDER_DIRECT_ENTRY_DIRECTION_FIELD = 'workOrderDirectEntryDirection';
export const WORK_ORDER_DIRECT_ENTRY_PRICE_MODE_FIELD = 'workOrderDirectEntryPriceMode';
export const WORK_ORDER_DIRECT_ENTRY_LINK_IDS_FIELD = 'workOrderDirectEntryLinkIds';
export const WORK_ORDER_DIRECT_ENTRY_ITEM_PRICES_FIELD = 'workOrderDirectEntryItemPrices';
export const WORK_ORDER_DIRECT_ENTRY_DESCRIPTION_MAX = 1000;
/** Kept for backward-compat stored values — UI no longer uses direction. */
export const WORK_ORDER_DIRECT_ENTRY_DIRECTIONS = ['none', 'sale', 'expense'] as const;
export type WorkOrderDirectEntryDirection = typeof WORK_ORDER_DIRECT_ENTRY_DIRECTIONS[number];
export type WorkOrderDirectEntryActiveDirection = 'sale' | 'expense';
/** Kept for backward compat — direct entry always persists `data`; `general` is unused. */
export const WORK_ORDER_DIRECT_ENTRY_PRICE_MODES = ['data', 'general'] as const;
export type WorkOrderDirectEntryPriceMode = typeof WORK_ORDER_DIRECT_ENTRY_PRICE_MODES[number];
export type GlAccountType = 'Income' | 'Expense';
/** Built-in kinds pickable on direct-entry catalog multiselect (no SKU / asset component). */
export const DIRECT_ENTRY_CATALOG_BUILTIN_KINDS = [
  'product', 'asset', 'service', 'product_group', 'asset_group', 'service_category',
] as const;
export const DIRECT_ENTRY_SELLABLE_KINDS = new Set(['product', 'asset', 'service']);
export const DIRECT_ENTRY_GROUP_KINDS = new Set([
  'product_group', 'asset_group', 'service_category',
]);

export const workOrderKindAllowsEmptyFormFields = (kind: WorkOrderKind): boolean => (
  kind === 'assets_mobility' || kind === 'document' || kind === 'directive'
  || kind === 'sale' || kind === 'expense' || kind === 'direct_entry' || kind === 'pos_sales'
);

/** Read-only directive page — description text only, no submit button. */
export const workOrderKindIsDirective = (kind: WorkOrderKind): boolean => (
  kind === 'directive'
);

export const workOrderKindUsesRecipientFields = (kind: WorkOrderKind): boolean => (
  kind === 'basic'
);

/** Sale and expense — money-in / money-out work orders linked to priced custom data. */
export const workOrderKindIsTransaction = (kind: WorkOrderKind): boolean => (
  kind === 'sale' || kind === 'expense'
);

/** Direct entry — optional catalog links + per-item prices; sign from each row’s GL accountType. */
export const workOrderKindIsDirectEntry = (kind: WorkOrderKind): boolean => (
  kind === 'direct_entry'
);

/** Any work order that logs a money value on submit (linked or direct entry). */
export const workOrderKindLogsTransaction = (kind: WorkOrderKind): boolean => (
  workOrderKindIsTransaction(kind) || workOrderKindIsDirectEntry(kind)
);

/** Work orders that link one custom data row (sale, expense, POS sales). Assets mobility picks assets on the form after dispatch. */
export const workOrderKindUsesDataLink = (kind: WorkOrderKind): boolean => (
  kind === 'sale' || kind === 'expense'
  || kind === 'pos_sales'
);

/** Assets mobility — assign/unassign assets with warehouse location on the operation. */
export const workOrderKindIsAssetsMobility = (kind: WorkOrderKind): boolean => (
  kind === 'assets_mobility'
);

/** activity_logs.action / event_type for assets mobility submits. */
export const ASSET_MOBILITY_LOG_TYPE = 'Asset mobility' as const;

/** Same master-data asset condition options (fields.condition) — used on mobility issue/return. */
export const ASSET_MOBILITY_CONDITION_OPTIONS = ASSET_CONDITION_OPTIONS;

export type AssetMobilityCondition = typeof ASSET_MOBILITY_CONDITION_OPTIONS[number];

export const normalizeAssetMobilityCondition = (raw: unknown): AssetMobilityCondition | '' => {
  const text = String(raw || '').trim();
  return (ASSET_MOBILITY_CONDITION_OPTIONS as readonly string[]).includes(text)
    ? text as AssetMobilityCondition
    : '';
};

/** Built-in kinds allowed as the assets mobility link (group or single asset). */
export const ASSET_MOBILITY_BUILTIN_LINK_KINDS = ['asset_group', 'asset'] as const;

export const isAssetMobilityBuiltinLinkKind = (kind: unknown): boolean => (
  (ASSET_MOBILITY_BUILTIN_LINK_KINDS as readonly string[]).includes(
    String(kind || '').trim().toLowerCase(),
  )
);

/**
 * @deprecated Mobility no longer ties a WO to an asset/group — use resolveAssetsMobilityFormAssets.
 * Kept for older sessions that still store a link id.
 */
export const resolveAssetsMobilityAssetsForLink = <T extends AssetRentingRecordLike>(
  linkRecord: T | null | undefined,
  records: T[],
): T[] => {
  if (!linkRecord?.id) return [];
  const kind = String(linkRecord.kind || '').trim();
  if (kind === 'asset') return [linkRecord];
  if (kind !== 'asset_group') return [];
  const groupId = String(linkRecord.id).trim();
  return records.filter((row) => (
    row.kind === 'asset'
    && parseCommaSeparatedRecordIds(row.fields?.assetGroup).includes(groupId)
  ));
};

/** All org assets on the mobility form — chosen after the operation is dispatched. */
export const resolveAssetsMobilityFormAssets = <T extends AssetRentingRecordLike>(
  records: T[],
): T[] => records
  .filter((row) => String(row.kind || '').trim() === 'asset')
  .slice()
  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));

/** Asset groups with member asset ids (asset.assetGroup CSV) for the mobility picker. */
export const resolveAssetsMobilityFormGroups = <T extends AssetRentingRecordLike>(
  records: T[],
): Array<{ id: string, name: string, code: string, assetIds: string[] }> => {
  const assets = records.filter((row) => String(row.kind || '').trim() === 'asset');
  return records
    .filter((row) => String(row.kind || '').trim() === 'asset_group')
    .map((group) => {
      const groupId = String(group.id || '').trim();
      const assetIds = assets
        .filter((row) => parseCommaSeparatedRecordIds(row.fields?.assetGroup).includes(groupId))
        .map((row) => String(row.id || '').trim())
        .filter(Boolean);
      const code = String(group.fields?.code || '').trim();
      return {
        id: groupId,
        name: String(group.name || ''),
        code,
        assetIds,
      };
    })
    .filter((row) => row.id && row.assetIds.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

/** True when the asset row has an assignee (checked out). */
export const assetMobilityAssetIsCheckedOut = (
  asset: AssetRentingRecordLike | null | undefined,
): boolean => Boolean(String(asset?.fields?.assignedTo || '').trim());

/** @deprecated Mobility WOs no longer use workOrderAssetLinkId — always null. */
export const assetsMobilityWorkOrderLinkError = (
  _linkRecord?: AssetRentingRecordLike | null,
  _records?: AssetRentingRecordLike[],
): string | null => null;

/** Assets mobility operations must declare location (warehouse) on the operation. */
export const workOrderKindRequiresOperationLocation = (kind: WorkOrderKind): boolean => (
  kind === 'assets_mobility'
);

/** Kinds that exclusive-lock workOrderAssetLinkId — none after asset renting removal (mobility picks at form). */
export const WORK_ORDER_ASSET_LINK_EXCLUSIVE_KINDS = [] as const;

export type WorkOrderAssetLinkExclusiveKind = typeof WORK_ORDER_ASSET_LINK_EXCLUSIVE_KINDS[number];

export const workOrderKindUsesExclusiveAssetLink = (kind: WorkOrderKind): boolean => (
  (WORK_ORDER_ASSET_LINK_EXCLUSIVE_KINDS as readonly string[]).includes(kind)
);

/** POS sales — full-screen register linked to a priced custom data catalog. */
export const workOrderKindIsPosSales = (kind: WorkOrderKind): boolean => (
  kind === 'pos_sales'
);

export type PosSalesPayMode = 'outright' | 'customer';

export const workOrderKindPriceField = (kind: WorkOrderKind): 'selling' | 'cost' | null => {
  if (kind === 'sale') return 'selling';
  if (kind === 'expense') return 'cost';
  return null;
};

/** Empty select → none; unknown values → null. */
export const normalizeWorkOrderDirectEntryDirection = (
  raw: unknown,
): WorkOrderDirectEntryDirection | null => {
  const value = String(raw || '').trim().toLowerCase();
  if (!value) return 'none';
  return (WORK_ORDER_DIRECT_ENTRY_DIRECTIONS as readonly string[]).includes(value)
    ? (value as WorkOrderDirectEntryDirection)
    : null;
};

/** Sale/expense only — price + link UI and money logs. */
export const workOrderDirectEntryDirectionIsActive = (
  dir: unknown,
): dir is WorkOrderDirectEntryActiveDirection => {
  const normalized = typeof dir === 'string' && (dir === 'sale' || dir === 'expense')
    ? dir
    : normalizeWorkOrderDirectEntryDirection(dir);
  return normalized === 'sale' || normalized === 'expense';
};

export const normalizeWorkOrderDirectEntryPriceMode = (
  raw: unknown,
): WorkOrderDirectEntryPriceMode | null => {
  const value = String(raw || '').trim().toLowerCase();
  return (WORK_ORDER_DIRECT_ENTRY_PRICE_MODES as readonly string[]).includes(value)
    ? (value as WorkOrderDirectEntryPriceMode)
    : null;
};

/** Strip thousand commas before numeric parse (e.g. 200,000.00 → 200000.00). */
export const stripWorkOrderDirectEntryPriceCommas = (raw: unknown): string => (
  String(raw ?? '').trim().replace(/,/g, '')
);

/** Parse a non-negative direct-entry price — accepts comma-formatted numbers; returns 2dp or error. */
export const parseWorkOrderDirectEntryPrice = (
  raw: unknown,
): { price?: string, error?: string } => {
  const value = stripWorkOrderDirectEntryPriceCommas(raw);
  if (!value) return { error: 'Enter a price (0 or greater)' };
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { error: 'Price must be 0 or greater' };
  }
  return { price: parsed.toFixed(2) };
};

/** Display price with thousand commas (e.g. 200000 → 200,000.00). */
export const formatWorkOrderDirectEntryPriceDisplay = (raw: unknown): string => {
  const pack = parseWorkOrderDirectEntryPrice(raw);
  if (!pack.price) {
    const fallback = String(raw ?? '').trim();
    return fallback;
  }
  const [intPart, decPart = '00'] = pack.price.split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${withCommas}.${decPart}`;
};

/** Prefixed + / − from GL accountType (Income +, Expense −); optional currency symbol. */
export const formatWorkOrderDirectEntrySignedPriceDisplay = (
  raw: unknown,
  accountType?: GlAccountType | null,
  currency?: string | null,
): string => {
  const abs = formatWorkOrderDirectEntryPriceDisplay(raw);
  if (!abs) return '';
  const cur = String(currency || '').trim();
  return `${accountType === 'Expense' ? '−' : '+'}${cur}${abs}`;
};

/** Signed net of direct-entry lines (Expense subtracts). */
export const sumDirectEntrySignedLineItemsNet = (
  items: TransactionWorkOrderLineItem[],
): number => (
  (items || []).reduce((sum, item) => {
    const n = parseFloat(stripWorkOrderDirectEntryPriceCommas(item.unitPrice)) || 0;
    const abs = Math.abs(n);
    return sum + (item.accountType === 'Expense' ? -abs : abs);
  }, 0)
);

/** Display signed net with commas and optional currency (e.g. −$50,000.00). */
export const formatDirectEntrySignedNetDisplay = (
  net: number,
  currency?: string | null,
): string => {
  const abs = formatWorkOrderDirectEntryPriceDisplay(Math.abs(net).toFixed(2));
  if (!abs) return `${String(currency || '').trim()}0.00`;
  const cur = String(currency || '').trim();
  if (net < 0) return `−${cur}${abs}`;
  if (net > 0) return `+${cur}${abs}`;
  return `${cur}${abs}`;
};

/** Comma-separated linked catalog ids for direct entry (same rules as other multi-id fields). */
export const parseCommaSeparatedDirectEntryLinkIds = (raw: unknown): string[] => {
  const text = String(raw ?? '').trim();
  if (!text) return [];
  return Array.from(new Set(
    text.split(',').map((part) => part.trim()).filter(Boolean),
  ));
};

/** Per-record staff prices for data mode — accepts object or JSON string. */
export const parseDirectEntryItemPrices = (raw: unknown): Record<string, string> => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const out: Record<string, string> = {};
    Object.entries(raw as Record<string, unknown>).forEach(([id, price]) => {
      const key = String(id || '').trim();
      if (!key) return;
      out[key] = String(price ?? '').trim();
    });
    return out;
  }
  const text = String(raw ?? '').trim();
  if (!text) return {};
  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parseDirectEntryItemPrices(parsed);
    }
  } catch {
    // Non-JSON stored values are treated as empty.
  }
  return {};
};

export const stringifyDirectEntryItemPrices = (map: Record<string, string>): string => {
  const clean: Record<string, string> = {};
  Object.entries(map || {}).forEach(([id, price]) => {
    const key = String(id || '').trim();
    if (!key) return;
    clean[key] = String(price ?? '').trim();
  });
  return JSON.stringify(clean);
};

/** Resolve sale vs expense for logging — kind sale/expense, or active direct_entry direction. */
export const resolveWorkOrderTransactionDirection = (
  kind: WorkOrderKind,
  fields?: { workOrderDirectEntryDirection?: unknown } | null,
): WorkOrderDirectEntryActiveDirection | null => {
  if (kind === 'sale') return 'sale';
  if (kind === 'expense') return 'expense';
  if (kind === 'direct_entry') {
    const dir = normalizeWorkOrderDirectEntryDirection(fields?.workOrderDirectEntryDirection);
    return workOrderDirectEntryDirectionIsActive(dir) ? dir : null;
  }
  return null;
};

export const normalizeWorkOrderKind = (raw: unknown): WorkOrderKind => {
  const value = String(raw || '').trim().toLowerCase();
  return (WORK_ORDER_KINDS as readonly string[]).includes(value)
    ? (value as WorkOrderKind)
    : 'basic';
};

/** Sale / expense / POS can link one custom master-data record (picture). Price comes from a related UOM. */
export type WorkOrderAssetLink = {
  /** Custom type code of the selected item (not SKU, not head-scope). */
  kind: string
  /** erp_records id of that custom data instance. */
  id: string
};

/** True when a string looks like a stored custom type code (non-empty, clipped). */
export const isWorkOrderAssetLinkKind = (raw: unknown): boolean => {
  const value = String(raw || '').trim();
  return Boolean(value) && value.length <= 80;
};

/** Asset link on work orders tracks as head-scope (same family as structural picks). */
export const WORK_ORDER_ASSET_LINK_TRACKING_ITEM = 'track_head_scope' as const;

/** Built-in catalog kinds allowed as the rented item (asset group, asset, asset component). */
export const ASSET_RENTING_BUILTIN_LINK_KINDS = ['asset_group', 'asset', 'asset_component'] as const;

export type AssetRentingRecordLike = {
  id?: string
  name?: string
  kind?: string
  parentId?: string | null
  fields?: Record<string, unknown> | null
};

export const isAssetRentingBuiltinLinkKind = (kind: unknown): boolean => (
  (ASSET_RENTING_BUILTIN_LINK_KINDS as readonly string[]).includes(
    String(kind || '').trim().toLowerCase(),
  )
);

/** Comma-separated master-data record ids (location managers, skuIds, assetComponentIds, …). */
export const parseCommaSeparatedRecordIds = (raw: unknown): string[] => {
  const text = String(raw ?? '').trim();
  if (!text) return [];
  return Array.from(new Set(
    text.split(',').map((part) => part.trim()).filter(Boolean),
  ));
};

/** True when a location has at least one manager employee id on fields.managers. */
export const locationHasAssignedManager = (
  location: { fields?: Record<string, unknown> | null } | null | undefined,
): boolean => parseCommaSeparatedRecordIds(location?.fields?.managers).length > 0;

/** Product → SKU links (skuIds field; legacy single skuId). */
export const resolveProductSkuIds = (
  fields: Record<string, unknown> | null | undefined,
): string[] => {
  const fromMulti = parseCommaSeparatedRecordIds(fields?.skuIds);
  if (fromMulti.length) return fromMulti;
  const legacy = String(fields?.skuId || '').trim();
  return legacy ? [legacy] : [];
};

/** Asset → component links (assetComponentIds field; legacy single assetComponentId). */
export const resolveAssetComponentIds = (
  fields: Record<string, unknown> | null | undefined,
): string[] => {
  const fromMulti = parseCommaSeparatedRecordIds(fields?.assetComponentIds);
  if (fromMulti.length) return fromMulti;
  const legacy = String(fields?.assetComponentId || '').trim();
  return legacy ? [legacy] : [];
};

/** Linked asset_component rows for an asset (field ids + parent_id children). */
export const resolveAssetRentingLinkedComponentRecords = <T extends {
  id?: string
  kind?: string
  parentId?: string | null
  fields?: Record<string, unknown> | null
}>(
  assetRecord: T | null | undefined,
  records: T[],
): T[] => {
  if (!assetRecord?.id) return [];
  const assetId = String(assetRecord.id).trim();
  const ids = new Set<string>(resolveAssetComponentIds(assetRecord.fields));
  records.forEach((row) => {
    if (String(row.parentId || '').trim() === assetId
      && String(row.kind || '').trim() === 'asset_component') {
      const id = String(row.id || '').trim();
      if (id) ids.add(id);
    }
  });
  return [...ids]
    .map((id) => records.find((row) => String(row.id || '').trim() === id))
    .filter((row): row is T => Boolean(row));
};

/**
 * Asset rental payment / log Value comes from activity_logs.value on booking saves
 * (price + customer id + stay facts). POS / asset renting bill UOM sellingPrice only.
 */
export type AssetRentingUomPrice = {
  uomName: string
  sellingPrice: string
};

/** UOM selling price only — never fall back to costPrice for POS / sale. */
const uomCatalogSellingPriceAmount = (
  fields?: Record<string, unknown> | null,
): string => String(fields?.sellingPrice ?? '').trim();

export const resolveSkuUomSellingPrice = (
  sku: { fields?: Record<string, unknown> | null } | null | undefined,
  records: Array<{
    id?: string
    kind?: string
    name?: string
    fields?: Record<string, unknown> | null
  }>,
): AssetRentingUomPrice | null => {
  if (!sku) return null;
  const fields = sku.fields || {};
  // Catalog SKUs / asset components store the UOM id (or legacy name) on `unit`.
  const unitRef = String(
    fields.unit || fields.uom || fields.unitOfMeasure || fields.unit_of_measure || '',
  ).trim();
  if (!unitRef) return null;
  const unitLower = unitRef.toLowerCase();
  const uom = records.find((row) => {
    const rowId = String(row.id || '').trim();
    if (rowId && rowId === unitRef) {
      return uomCatalogSellingPriceAmount(row.fields) !== '';
    }
    const name = String(row.name || '').trim();
    const code = String(row.fields?.code || '').trim();
    if (name !== unitRef && code !== unitRef
      && name.toLowerCase() !== unitLower
      && code.toLowerCase() !== unitLower) {
      return false;
    }
    return uomCatalogSellingPriceAmount(row.fields) !== '';
  });
  if (!uom) return null;
  return {
    uomName: String(uom.name || unitRef).trim(),
    sellingPrice: uomCatalogSellingPriceAmount(uom.fields),
  };
};

/** Sum UOM selling prices for every component linked on a room asset. */
export const sumAssetRentingComponentSellingPrices = (
  assetRecord: { fields?: Record<string, unknown> | null } | null | undefined,
  records: Array<{
    id?: string
    kind?: string
    parentId?: string | null
    name?: string
    fields?: Record<string, unknown> | null
  }>,
): number => (
  resolveAssetRentingLinkedComponentRecords(
    assetRecord as { id?: string, kind?: string, parentId?: string | null, fields?: Record<string, unknown> | null },
    records,
  ).reduce((sum, component) => {
    const priced = resolveSkuUomSellingPrice(component, records);
    const parsed = parseFloat(String(priced?.sellingPrice || '').trim());
    return sum + (Number.isFinite(parsed) ? parsed : 0);
  }, 0)
);

/** True when the row itself carries a UOM that has a selling price. */
export const recordHasAssetRentingUomPrice = <T extends {
  fields?: Record<string, unknown> | null
}>(
  row: T | null | undefined,
  records: Array<{
    kind?: string
    name?: string
    fields?: Record<string, unknown> | null
  }>,
): boolean => Boolean(resolveSkuUomSellingPrice(row, records));

export type TransactionWorkOrderUomPrice = {
  uomName: string
  price: string
};

/** UOM selling or cost price for a SKU/component/custom row (walks unit → UOM record). */
export const resolveRecordUomPrice = (
  sku: { fields?: Record<string, unknown> | null } | null | undefined,
  records: Array<{
    id?: string
    kind?: string
    name?: string
    fields?: Record<string, unknown> | null
  }>,
  priceKind: 'selling' | 'cost',
): TransactionWorkOrderUomPrice | null => {
  if (!sku) return null;
  const fields = sku.fields || {};
  const unitRef = String(
    fields.unit || fields.uom || fields.unitOfMeasure || fields.unit_of_measure || '',
  ).trim();
  if (!unitRef) return null;
  const unitLower = unitRef.toLowerCase();
  const uom = records.find((row) => {
    const rowId = String(row.id || '').trim();
    const amount = priceKind === 'cost'
      ? String(row.fields?.costPrice ?? '').trim()
      : uomCatalogSellingPriceAmount(row.fields);
    if (rowId && rowId === unitRef) {
      return amount !== '';
    }
    const name = String(row.name || '').trim();
    const code = String(row.fields?.code || '').trim();
    if (name !== unitRef && code !== unitRef
      && name.toLowerCase() !== unitLower
      && code.toLowerCase() !== unitLower) {
      return false;
    }
    return amount !== '';
  });
  if (!uom) return null;
  const price = priceKind === 'cost'
    ? String(uom.fields?.costPrice ?? '').trim()
    : uomCatalogSellingPriceAmount(uom.fields);
  return {
    uomName: String(uom.name || unitRef).trim(),
    price,
  };
};

export const recordHasTransactionUomPrice = <T extends {
  fields?: Record<string, unknown> | null
}>(
  row: T | null | undefined,
  records: Array<{
    id?: string
    kind?: string
    name?: string
    fields?: Record<string, unknown> | null
  }>,
  priceKind: 'selling' | 'cost',
): boolean => Boolean(resolveRecordUomPrice(row, records, priceKind));

export type TransactionWorkOrderLineItem = {
  recordId: string
  recordName: string
  recordCode: string
  recordKind: string
  uomName: string
  unitPrice: string
  /** Direct entry — Income → positive Sale log; Expense → negative Expense work order log. */
  accountType?: GlAccountType | null
};

const lineItemFromRecord = <T extends {
  id?: string
  kind?: string
  name?: string
  fields?: Record<string, unknown> | null
}>(
  row: T,
  price: TransactionWorkOrderUomPrice,
): TransactionWorkOrderLineItem => ({
  recordId: String(row.id || '').trim(),
  recordName: String(row.name || '').trim(),
  recordCode: String(row.fields?.code || '').trim(),
  recordKind: String(row.kind || '').trim(),
  uomName: price.uomName,
  unitPrice: price.price,
});

/** Sum line-item unit prices (numeric parse; falls back to 0). */
export const sumTransactionWorkOrderLineItems = (
  items: TransactionWorkOrderLineItem[],
): string => {
  const total = items.reduce((sum, item) => sum + (parseFloat(item.unitPrice) || 0), 0);
  return Number.isFinite(total) ? total.toFixed(2) : '0.00';
};

/** Resolve priced rows for sale/expense links — direct row, connected SKU, or group children. */
export const resolveTransactionWorkOrderPricedLineItems = <T extends AssetRentingRecordLike>(
  linkRecord: T | null | undefined,
  records: T[],
  skuTypeCodes: Iterable<string> | null | undefined,
  priceKind: 'selling' | 'cost',
): TransactionWorkOrderLineItem[] => {
  if (!linkRecord?.id) return [];

  const pricedRow = (row: T | null | undefined): TransactionWorkOrderLineItem | null => {
    if (!row) return null;
    const price = resolveRecordUomPrice(row, records, priceKind);
    return price ? lineItemFromRecord(row, price) : null;
  };

  const direct = pricedRow(linkRecord);
  if (direct) return [direct];

  // Products have no unit field — price each linked SKU’s UOM.
  if (String(linkRecord.kind || '').trim().toLowerCase() === 'product') {
    const skuItems: TransactionWorkOrderLineItem[] = [];
    resolveProductSkuIds(linkRecord.fields).forEach((skuId) => {
      const sku = records.find((row) => String(row.id || '').trim() === skuId);
      const item = pricedRow(sku as T | undefined);
      if (item) skuItems.push(item);
    });
    if (skuItems.length) return skuItems;
  }

  const pricingRow = resolveAssetRentingPricingSku(linkRecord, records, skuTypeCodes);
  const fromConnected = pricedRow(pricingRow);
  if (fromConnected) return [fromConnected];

  const linkId = String(linkRecord.id).trim();
  const children = records.filter((row) => String(row.parentId || '').trim() === linkId);
  const childItems: TransactionWorkOrderLineItem[] = [];
  children.forEach((child) => {
    const item = pricedRow(child);
    if (item) {
      childItems.push(item);
      return;
    }
    const nested = resolveAssetRentingPricingSku(child, records, skuTypeCodes);
    const nestedItem = pricedRow(nested);
    if (nestedItem) childItems.push(nestedItem);
  });
  if (childItems.length) return childItems;

  return [];
};

/** Effective total — always the sum of line-item prices (override removed). */
export const resolveTransactionWorkOrderEffectiveTotal = (
  lineItems: TransactionWorkOrderLineItem[],
  _overrideRaw?: unknown,
): { computedTotal: string, effectiveTotal: string, totalWasOverridden: boolean } => {
  const computedTotal = sumTransactionWorkOrderLineItems(lineItems);
  return { computedTotal, effectiveTotal: computedTotal, totalWasOverridden: false };
};

/**
 * Build direct-entry line items from selected ids — always data mode (staff prices per row).
 * Attaches accountType from each row’s GL when resolvable.
 */
export const resolveDirectEntryWorkOrderLineItems = <T extends AssetRentingRecordLike>(
  args: {
    linkIds: string[]
    /** Ignored — always treated as data (kept for call-site compat). */
    priceMode?: WorkOrderDirectEntryPriceMode | null | undefined
    itemPrices?: Record<string, string> | null
    records: T[]
  },
): { lineItems: TransactionWorkOrderLineItem[], error?: string } => {
  const linkIds = (args.linkIds || []).map((id) => String(id || '').trim()).filter(Boolean);
  if (!linkIds.length) return { lineItems: [] };
  const records = args.records || [];
  const byId = new Map(records.map((row) => [String(row.id || '').trim(), row]));
  const itemPrices = args.itemPrices || {};
  const lineItems: TransactionWorkOrderLineItem[] = [];
  for (const id of linkIds) {
    const row = byId.get(id);
    if (!row) return { lineItems: [], error: 'A linked record was not found' };
    const label = String(row.name || id).trim() || id;
    const pack = parseWorkOrderDirectEntryPrice(itemPrices[id]);
    if (pack.error) return { lineItems: [], error: `Enter a valid price for ${label}` };
    const uomHint = resolveRecordUomPrice(row, records, 'cost')
      || resolveRecordUomPrice(row, records, 'selling');
    const accountType = resolveRecordGlAccountType(row, records);
    lineItems.push({
      recordId: id,
      recordName: String(row.name || '').trim(),
      recordCode: String(row.fields?.code || '').trim(),
      recordKind: String(row.kind || '').trim(),
      uomName: uomHint?.uomName || 'unit',
      unitPrice: pack.price!,
      accountType,
    });
  }
  return { lineItems };
};

/**
 * Prefill price for a newly selected catalog row — UOM selling sum + optional cost increase,
 * else service.price, else group/category child sum (GL-signed).
 * Returns comma-formatted display string.
 */
export const resolveDirectEntryDefaultItemPrice = <T extends AssetRentingRecordLike>(
  row: T | null | undefined,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): string => {
  if (!row?.id) return '';
  const kind = String(row.kind || '').trim().toLowerCase();
  // Service categories / product & asset groups → sum member prices (Income +, Expense −).
  if (DIRECT_ENTRY_GROUP_KINDS.has(kind)) {
    const pack = resolveDirectEntryGroupDefaultPrice(row, records, skuTypeCodes);
    return pack.display;
  }
  return resolveDirectEntrySellableDefaultItemPrice(row, records, skuTypeCodes);
};

/** Absolute/unit price for one sellable catalog row (no group walk). */
const resolveDirectEntrySellableDefaultItemPrice = <T extends AssetRentingRecordLike>(
  row: T | null | undefined,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): string => {
  if (!row?.id) return '';
  // Bill from UOM sellingPrice — never costPrice.
  const sellingTotal = sumRecordUomSellingTotal(row, records, skuTypeCodes);
  if (sellingTotal > 0) {
    const increase = resolveCostIncreaseForRecord(row, records);
    const unitPrice = applyCostIncreaseToCost(sellingTotal, increase);
    if (unitPrice > 0) return formatWorkOrderDirectEntryPriceDisplay(unitPrice.toFixed(2));
  }
  const servicePrice = String(row.fields?.price || '').trim();
  if (servicePrice) {
    const pack = parseWorkOrderDirectEntryPrice(servicePrice);
    if (pack.price) return formatWorkOrderDirectEntryPriceDisplay(pack.price);
  }
  return '';
};

/**
 * Combined default for a group/category: sum member prices with GL sign
 * (Income adds, Expense subtracts). Display is abs(net); accountType follows net.
 */
export const resolveDirectEntryGroupDefaultPrice = <T extends AssetRentingRecordLike>(
  groupRow: T | null | undefined,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): { display: string, net: number, accountType: GlAccountType } => {
  const members = resolveDirectEntryGroupSellableMembers(groupRow, records);
  let net = 0;
  members.forEach((member) => {
    const raw = resolveDirectEntrySellableDefaultItemPrice(member, records, skuTypeCodes);
    const pack = parseWorkOrderDirectEntryPrice(raw);
    const amount = pack.price ? parseFloat(pack.price) : 0;
    if (!Number.isFinite(amount) || amount <= 0) return;
    const gl = resolveGlAccountTypeFromRef(member.fields?.glAccount, records);
    // Expense GL reduces the combined charge; Income (or missing) adds.
    net += gl === 'Expense' ? -amount : amount;
  });
  const accountType: GlAccountType = net < 0 ? 'Expense' : 'Income';
  const abs = Math.abs(net);
  return {
    net,
    accountType,
    display: abs > 0 ? formatWorkOrderDirectEntryPriceDisplay(abs.toFixed(2)) : '',
  };
};

/** Flatten sellable products/assets/services under a group (walks nested categories). */
export const resolveDirectEntryGroupSellableMembers = <T extends AssetRentingRecordLike>(
  groupRow: T | null | undefined,
  records: T[],
): T[] => {
  if (!groupRow?.id) return [];
  const out: T[] = [];
  const seen = new Set<string>();
  const walk = (node: T) => {
    const children = recordsScopedUnderDirectEntryGroup(node, records);
    children.forEach((child) => {
      const id = String(child.id || '').trim();
      if (!id || seen.has(id)) return;
      seen.add(id);
      const kind = String(child.kind || '').trim().toLowerCase();
      if (DIRECT_ENTRY_SELLABLE_KINDS.has(kind)) {
        out.push(child);
        return;
      }
      if (DIRECT_ENTRY_GROUP_KINDS.has(kind)) walk(child);
    });
  };
  walk(groupRow);
  return out;
};

/** Children of a direct-entry group via parent_id and/or scope field (serviceCategory, …). */
const recordsScopedUnderDirectEntryGroup = <T extends AssetRentingRecordLike>(
  groupRecord: T,
  records: T[],
): T[] => {
  const groupId = String(groupRecord.id || '').trim();
  if (!groupId) return [];
  const groupKind = String(groupRecord.kind || '').trim().toLowerCase();
  const seen = new Set<string>();
  const out: T[] = [];
  const push = (row: T) => {
    const id = String(row.id || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(row);
  };
  (records || []).forEach((row) => {
    if (String(row.parentId || '').trim() === groupId) push(row);
  });
  (records || []).forEach((row) => {
    const scope = recordScopeGroupField(String(row.kind || '').trim() as RecordKind);
    if (!scope || scope.scopeKind !== groupKind) return;
    if (parseCommaSeparatedRecordIds(row.fields?.[scope.fieldKey]).includes(groupId)) push(row);
  });
  return out;
};

/** Lookup gl_account by id, name, or code and return Income | Expense. */
export const resolveGlAccountTypeFromRef = <T extends AssetRentingRecordLike>(
  glAccountRef: unknown,
  records: T[],
): GlAccountType | null => {
  const ref = String(glAccountRef || '').trim();
  if (!ref) return null;
  const gl = (records || []).find((r) => {
    if (String(r.kind || '').trim().toLowerCase() !== 'gl_account') return false;
    const id = String(r.id || '').trim();
    const name = String(r.name || '').trim();
    const code = String(r.fields?.code || '').trim();
    return id === ref
      || name === ref
      || (code && code.toUpperCase() === ref.toUpperCase());
  });
  const t = String(gl?.fields?.accountType || '').trim();
  return t === 'Income' || t === 'Expense' ? t : null;
};

/**
 * Resolve Income|Expense for a catalog row.
 * Groups: own GL if set, else net sign from member GL charges; sellables without GL → null.
 */
export const resolveRecordGlAccountType = <T extends AssetRentingRecordLike>(
  row: T | null | undefined,
  records: T[],
): GlAccountType | null => {
  if (!row) return null;
  const fromField = resolveGlAccountTypeFromRef(row.fields?.glAccount, records);
  if (fromField) return fromField;
  const kind = String(row.kind || '').trim().toLowerCase();
  if (DIRECT_ENTRY_GROUP_KINDS.has(kind)) {
    return resolveDirectEntryGroupDefaultPrice(row, records).accountType;
  }
  return null;
};

/** Validate GL accountType for each direct-entry line — sellables must have Income|Expense. */
export const resolveDirectEntryLineAccountTypesError = <T extends AssetRentingRecordLike>(
  lineItems: TransactionWorkOrderLineItem[],
  records: T[],
): string | null => {
  const byId = new Map((records || []).map((r) => [String(r.id || '').trim(), r]));
  for (const item of lineItems) {
    const row = byId.get(String(item.recordId || '').trim());
    const label = wrapDirectEntryLineLabel(item);
    const kind = String(item.recordKind || row?.kind || '').trim().toLowerCase();
    // Groups must be expanded to members before logging — never log the group itself.
    if (DIRECT_ENTRY_GROUP_KINDS.has(kind)) {
      return `${label} must be expanded to its products/assets/services before logging`;
    }
    const accountType = item.accountType
      || (row ? resolveRecordGlAccountType(row, records) : null);
    if (accountType === 'Income' || accountType === 'Expense') {
      item.accountType = accountType;
      continue;
    }
    return `${label} needs a GL account with Income or Expense type`;
  }
  return null;
};

/**
 * Expand service categories / product & asset groups into one line per member.
 * Staff group total is split across members by their catalog prices; each line uses that member’s GL.
 */
export const expandDirectEntryLineItemsForLogs = <T extends AssetRentingRecordLike>(
  lineItems: TransactionWorkOrderLineItem[],
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): { lineItems: TransactionWorkOrderLineItem[], error?: string } => {
  const byId = new Map((records || []).map((r) => [String(r.id || '').trim(), r]));
  const out: TransactionWorkOrderLineItem[] = [];

  for (const item of lineItems || []) {
    const row = byId.get(String(item.recordId || '').trim());
    const kind = String(item.recordKind || row?.kind || '').trim().toLowerCase();
    if (!DIRECT_ENTRY_GROUP_KINDS.has(kind)) {
      const accountType = (row ? resolveRecordGlAccountType(row, records) : null)
        || item.accountType
        || null;
      out.push({
        ...item,
        accountType: accountType === 'Expense' || accountType === 'Income' ? accountType : item.accountType,
      });
      continue;
    }
    if (!row) {
      return { lineItems: [], error: `${wrapDirectEntryLineLabel(item)} was not found` };
    }
    const members = resolveDirectEntryGroupSellableMembers(row, records);
    const groupLabel = wrapDirectEntryLineLabel({
      ...item,
      recordName: String(row.name || item.recordName || '').trim(),
      recordCode: String(row.fields?.code || item.recordCode || '').trim(),
    });
    if (!members.length) {
      return { lineItems: [], error: `${groupLabel} has no products, assets, or services to log` };
    }

    // Member catalog prices (absolute) — used to split any staff override on the group.
    const memberDefaults = members.map((member) => {
      const raw = resolveDirectEntryDefaultItemPrice(member, records, skuTypeCodes);
      const pack = parseWorkOrderDirectEntryPrice(raw);
      const amount = pack.price ? parseFloat(pack.price) : 0;
      return {
        member,
        amount: Number.isFinite(amount) && amount > 0 ? amount : 0,
      };
    });
    const defaultAbsSum = memberDefaults.reduce((sum, row) => sum + row.amount, 0);
    const enteredPack = parseWorkOrderDirectEntryPrice(item.unitPrice);
    const enteredAbs = enteredPack.price ? parseFloat(enteredPack.price) : 0;
    // Scale so member amounts sum to the group price staff set (falls back to catalog totals).
    const scale = defaultAbsSum > 0 && enteredAbs > 0
      ? enteredAbs / defaultAbsSum
      : 1;
    const equalShare = members.length > 0 && enteredAbs > 0 && defaultAbsSum <= 0
      ? enteredAbs / members.length
      : 0;

    for (const { member, amount } of memberDefaults) {
      const base = amount > 0 ? amount * scale : equalShare;
      if (!(base > 0)) {
        const label = wrapLogStyleLabel(member);
        return { lineItems: [], error: `${label} in ${groupLabel} needs a price` };
      }
      const accountType = resolveRecordGlAccountType(member, records);
      if (accountType !== 'Income' && accountType !== 'Expense') {
        const label = wrapLogStyleLabel(member);
        return { lineItems: [], error: `${label} needs a GL account with Income or Expense type` };
      }
      const uomHint = resolveRecordUomPrice(member, records, 'cost')
        || resolveRecordUomPrice(member, records, 'selling');
      out.push({
        recordId: String(member.id || '').trim(),
        recordName: String(member.name || '').trim(),
        recordCode: String(member.fields?.code || '').trim(),
        recordKind: String(member.kind || '').trim(),
        uomName: uomHint?.uomName || 'unit',
        unitPrice: base.toFixed(2),
        accountType,
      });
    }
  }

  return { lineItems: out };
};

const wrapLogStyleLabel = (row: AssetRentingRecordLike): string => {
  const name = String(row.name || '').trim();
  const code = String(row.fields?.code || row.id || '').trim();
  if (name && code) return `${name}(${code})`;
  return name || code || 'Catalog row';
};

const wrapDirectEntryLineLabel = (item: TransactionWorkOrderLineItem): string => {
  const name = String(item.recordName || '').trim();
  const code = String(item.recordCode || item.recordId || '').trim();
  if (name && code) return `${name}(${code})`;
  return name || code || 'Catalog row';
};

/** Validation for direct_entry — description optional; links optional; prices required per linked row. */
export const workOrderDirectEntryFieldsError = (
  fields: {
    description?: unknown
    workOrderDirectEntryPrice?: unknown
    workOrderDirectEntryDirection?: unknown
    workOrderDirectEntryPriceMode?: unknown
    workOrderDirectEntryLinkIds?: unknown
    workOrderDirectEntryItemPrices?: unknown
  },
  records?: AssetRentingRecordLike[],
): string | null => {
  const desc = String(fields.description || '').trim();
  if (desc.length > WORK_ORDER_DIRECT_ENTRY_DESCRIPTION_MAX) {
    return `Description must be ${WORK_ORDER_DIRECT_ENTRY_DESCRIPTION_MAX} characters or fewer`;
  }
  // No linked rows → no money UI / logs (ok).
  const linkIds = parseCommaSeparatedDirectEntryLinkIds(fields.workOrderDirectEntryLinkIds);
  if (!linkIds.length) return null;
  const resolved = resolveDirectEntryWorkOrderLineItems({
    linkIds,
    priceMode: 'data',
    itemPrices: parseDirectEntryItemPrices(fields.workOrderDirectEntryItemPrices),
    records: records || [],
  });
  if (resolved.error) return resolved.error;
  return null;
};

/** Catalog record id on product/asset — optional cost_increase configuration. */
export const COST_INCREASE_RECORD_FIELD = 'costIncreaseId';

export const COST_INCREASE_MODES = ['Percentage', 'Fixed amount'] as const;
export type CostIncreaseMode = typeof COST_INCREASE_MODES[number];

export type CostIncreaseConfig = {
  mode: CostIncreaseMode;
  value: number;
};

/** Parse a cost_increase configuration record — missing/invalid → zero increase. */
export const parseCostIncreaseConfig = (
  increaseRecord: { fields?: Record<string, unknown> | null } | null | undefined,
): CostIncreaseConfig => {
  if (!increaseRecord?.fields) return { mode: 'Percentage', value: 0 };
  const modeRaw = String(increaseRecord.fields.increaseMode || increaseRecord.fields.markupMode || '').trim();
  const mode = (COST_INCREASE_MODES as readonly string[]).includes(modeRaw)
    ? (modeRaw as CostIncreaseMode)
    : 'Percentage';
  const parsed = parseFloat(String(
    increaseRecord.fields.increaseValue ?? increaseRecord.fields.markupValue ?? '',
  ).trim());
  const value = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  return { mode, value };
};

/** Apply optional cost increase to a UOM cost total (0 when unset). */
export const applyCostIncreaseToCost = (
  costTotal: number,
  increase?: CostIncreaseConfig | null,
): number => {
  const base = Number.isFinite(costTotal) ? Math.max(0, costTotal) : 0;
  const cfg = increase || { mode: 'Percentage' as CostIncreaseMode, value: 0 };
  if (!cfg.value) return base;
  if (cfg.mode === 'Fixed amount') return base + cfg.value;
  return base * (1 + (cfg.value / 100));
};

const parseUomPriceAmount = (
  row: { fields?: Record<string, unknown> | null } | null | undefined,
  records: Array<{
    id?: string;
    kind?: string;
    name?: string;
    fields?: Record<string, unknown> | null;
  }>,
  priceKind: 'selling' | 'cost',
): number => {
  const priced = resolveRecordUomPrice(row, records, priceKind);
  if (!priced) return 0;
  const parsed = parseFloat(String(priced.price || '').trim());
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

/** Sum UOM prices for a catalog row (direct UOM, product SKUs, or asset components). */
export const sumRecordUomPriceTotal = <T extends AssetRentingRecordLike>(
  row: T | null | undefined,
  records: T[],
  priceKind: 'selling' | 'cost',
  skuTypeCodes?: Iterable<string> | null,
): number => {
  if (!row?.id) return 0;
  const kind = String(row.kind || '').trim().toLowerCase();
  const parse = (r: T | null | undefined) => parseUomPriceAmount(r, records, priceKind);

  if (kind === 'product') {
    const skuIds = resolveProductSkuIds(row.fields);
    // Product UOM always comes from linked SKUs — never a product.unit field.
    return skuIds.reduce((sum, skuId) => {
      const sku = records.find((r) => String(r.id || '').trim() === skuId);
      return sum + parse(sku);
    }, 0);
  }

  if (kind === 'asset') {
    const components = resolveAssetRentingLinkedComponentRecords(row, records);
    if (components.length) {
      return components.reduce((sum, component) => sum + parse(component), 0);
    }
    return parse(row);
  }

  if (isAssetRentingSkuRecordKind(row.kind, skuTypeCodes) || kind === 'sku' || kind === 'asset_component') {
    return parse(row);
  }

  const direct = parse(row);
  if (direct > 0) return direct;

  const pricingSku = resolveAssetRentingPricingSku(row, records, skuTypeCodes);
  return parse(pricingSku);
};

/** Sum UOM cost prices (catalog cost / margin — not POS bill amount). */
export const sumRecordUomCostTotal = <T extends AssetRentingRecordLike>(
  row: T | null | undefined,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): number => sumRecordUomPriceTotal(row, records, 'cost', skuTypeCodes);

/** Sum UOM selling prices — POS sales and product selling-price preview. */
export const sumRecordUomSellingTotal = <T extends AssetRentingRecordLike>(
  row: T | null | undefined,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): number => sumRecordUomPriceTotal(row, records, 'selling', skuTypeCodes);

export const resolveCostIncreaseForRecord = <T extends AssetRentingRecordLike>(
  row: T | null | undefined,
  records: T[],
): CostIncreaseConfig => {
  if (!row?.fields) return { mode: 'Percentage', value: 0 };
  const increaseId = String(
    row.fields[COST_INCREASE_RECORD_FIELD] || row.fields.salesMarkupId || '',
  ).trim();
  if (!increaseId) return { mode: 'Percentage', value: 0 };
  const increaseRecord = records.find((r) => String(r.id || '').trim() === increaseId) || null;
  return parseCostIncreaseConfig(increaseRecord);
};

export type PosSalesCatalogGroup = {
  recordId: string;
  recordName: string;
};

export type PosSalesCatalogItem = {
  recordId: string;
  recordName: string;
  recordCode: string;
  recordKind: string;
  photoUrl: string;
  /** Primary barcode (product/asset field, or first linked SKU barcode). */
  barcode: string;
  /** All scannable codes on this row (product/asset + linked SKU barcodes). */
  barcodes: string[];
  /** Product group / asset group id this row is scoped under. */
  groupId: string;
  /** Display label for the group chip row under the product name. */
  groupName: string;
  /** On-hand units for products; null when not a product row. */
  stock: number | null;
  costTotal: string;
  unitPrice: string;
};

/** Group/category rows in a POS catalog — not sellable on their own. */
export const POS_SALES_CONTAINER_KINDS = new Set([
  'product_group',
  'asset_group',
  'service_category',
]);

export const isPosSalesContainerKind = (kind: string): boolean => (
  POS_SALES_CONTAINER_KINDS.has(String(kind || '').trim().toLowerCase())
);

const resolveScopeGroupIdForRecord = <T extends AssetRentingRecordLike>(
  row: T,
): string => {
  const scope = recordScopeGroupField(String(row.kind || '').trim() as RecordKind);
  if (!scope) return '';
  // Multi-group CSV — first id is enough for a single nav/group label.
  return parseCommaSeparatedRecordIds(row.fields?.[scope.fieldKey])[0] || '';
};

const resolveRecordGroupLabel = <T extends AssetRentingRecordLike>(
  row: T,
  records: T[],
): { groupId: string; groupName: string } => {
  const groupId = resolveScopeGroupIdForRecord(row);
  if (!groupId) return { groupId: '', groupName: '' };
  const group = records.find((r) => String(r.id || '').trim() === groupId) || null;
  return {
    groupId,
    groupName: group ? String(group.name || '').trim() : '',
  };
};

const resolveRecordBarcodes = <T extends AssetRentingRecordLike>(
  row: T,
  records: T[],
): string[] => {
  const codes = new Set<string>();
  const push = (raw: unknown) => {
    const code = String(raw || '').trim();
    if (code) codes.add(code);
  };
  push(row.fields?.barcode);
  const kind = String(row.kind || '').trim().toLowerCase();
  if (kind === 'product') {
    resolveProductSkuIds(row.fields).forEach((skuId) => {
      const sku = records.find((r) => String(r.id || '').trim() === skuId);
      if (sku) push(sku.fields?.barcode);
    });
  }
  return Array.from(codes);
};

const parseProductStockField = (
  kind: string,
  fields: Record<string, unknown>,
): number | null => {
  if (String(kind || '').trim().toLowerCase() !== 'product') return null;
  const raw = String(fields.stock ?? '').trim();
  if (!raw) return 0;
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const posSalesCatalogItemFromRecord = <T extends AssetRentingRecordLike>(
  row: T,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): PosSalesCatalogItem | null => {
  if (isPosSalesContainerKind(String(row.kind || ''))) return null;
  // POS shelf price = UOM sellingPrice sum (+ optional product cost increase), never costPrice.
  const sellingTotal = sumRecordUomSellingTotal(row, records, skuTypeCodes);
  if (sellingTotal <= 0) return null;
  const increase = resolveCostIncreaseForRecord(row, records);
  const unitPrice = applyCostIncreaseToCost(sellingTotal, increase);
  const costTotal = sumRecordUomCostTotal(row, records, skuTypeCodes);
  const fields = row.fields || {};
  const barcodes = resolveRecordBarcodes(row, records);
  const { groupId, groupName } = resolveRecordGroupLabel(row, records);
  const recordKind = String(row.kind || '').trim();
  return {
    recordId: String(row.id || '').trim(),
    recordName: String(row.name || '').trim(),
    recordCode: String(fields.code || '').trim(),
    recordKind,
    photoUrl: String(fields.photoUrl || fields.pictureUrl || '').trim(),
    barcode: barcodes[0] || String(fields.barcode || '').trim(),
    barcodes,
    groupId,
    groupName,
    stock: parseProductStockField(recordKind, fields),
    costTotal: costTotal.toFixed(2),
    unitPrice: unitPrice.toFixed(2),
  };
};

/** Child rows under a built-in group — parent_id and/or scope field (e.g. productGroup). */
const recordsScopedUnderBuiltInGroup = <T extends AssetRentingRecordLike>(
  groupRecord: T,
  records: T[],
): T[] => {
  const groupId = String(groupRecord.id || '').trim();
  if (!groupId) return [];
  const groupKind = String(groupRecord.kind || '').trim().toLowerCase();
  const seen = new Set<string>();
  const out: T[] = [];
  const push = (row: T) => {
    const id = String(row.id || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(row);
  };

  records.forEach((row) => {
    if (String(row.parentId || '').trim() === groupId) push(row);
  });
  records.forEach((row) => {
    const scope = recordScopeGroupField(String(row.kind || '').trim() as RecordKind);
    if (!scope || scope.scopeKind !== groupKind) return;
    if (parseCommaSeparatedRecordIds(row.fields?.[scope.fieldKey]).includes(groupId)) push(row);
  });
  return out;
};

/** Nav group labels for a linked catalog root (root + immediate child groups). */
export const resolvePosSalesCatalogGroups = <T extends AssetRentingRecordLike>(
  linkRecord: T | null | undefined,
  records: T[],
): PosSalesCatalogGroup[] => {
  if (!linkRecord?.id || !isPosSalesContainerKind(String(linkRecord.kind || ''))) return [];
  const rootId = String(linkRecord.id).trim();
  const rootName = String(linkRecord.name || '').trim() || 'All';
  const out: PosSalesCatalogGroup[] = [{ recordId: rootId, recordName: rootName }];
  recordsScopedUnderBuiltInGroup(linkRecord, records)
    .filter((row) => isPosSalesContainerKind(String(row.kind || '')))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    .forEach((child) => {
      out.push({
        recordId: String(child.id || '').trim(),
        recordName: String(child.name || '').trim(),
      });
    });
  return out;
};

/** Flatten linked data / group into sellable POS rows (UOM selling price + optional sales markup). */
export const resolvePosSalesCatalogItems = <T extends AssetRentingRecordLike>(
  linkRecord: T | null | undefined,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): PosSalesCatalogItem[] => {
  if (!linkRecord?.id) return [];

  const out: PosSalesCatalogItem[] = [];
  const seen = new Set<string>();

  const pushRow = (row: T | null | undefined) => {
    if (!row?.id) return;
    const id = String(row.id).trim();
    if (!id || seen.has(id)) return;
    const item = posSalesCatalogItemFromRecord(row, records, skuTypeCodes);
    if (!item) return;
    seen.add(id);
    out.push(item);
  };

  const walkContainer = (container: T) => {
    recordsScopedUnderBuiltInGroup(container, records).forEach((row) => {
      if (isPosSalesContainerKind(String(row.kind || ''))) {
        walkContainer(row);
        return;
      }
      pushRow(row);
    });
  };

  if (isPosSalesContainerKind(String(linkRecord.kind || ''))) {
    walkContainer(linkRecord);
  } else {
    pushRow(linkRecord);
  }

  return out.sort((a, b) => a.recordName.localeCompare(b.recordName));
};

/** User-facing validation for POS sales custom data links. */
export const posSalesWorkOrderLinkPricingError = (
  linkRecord: AssetRentingRecordLike | null | undefined,
  records: AssetRentingRecordLike[],
  skuTypeCodes?: Iterable<string> | null,
): string | null => {
  if (!linkRecord?.id) return 'Pick a custom data catalog for POS sales';
  const items = resolvePosSalesCatalogItems(linkRecord, records, skuTypeCodes);
  if (items.length) return null;
  return 'That catalog needs a UOM with selling price, or at least one child row with UOM selling price';
};

/** User-facing validation for sale/expense custom data links. */
export const transactionWorkOrderLinkPricingError = (
  linkRecord: AssetRentingRecordLike | null | undefined,
  records: AssetRentingRecordLike[],
  skuTypeCodes: Iterable<string> | null | undefined,
  priceKind: 'selling' | 'cost',
): string | null => {
  if (!linkRecord?.id) return 'Pick a custom data item for this work order';
  const items = resolveTransactionWorkOrderPricedLineItems(
    linkRecord,
    records,
    skuTypeCodes,
    priceKind,
  );
  if (items.length) return null;
  const priceLabel = priceKind === 'cost' ? 'cost price' : 'selling price';
  return `That item needs a UOM with a ${priceLabel}, or at least one child data row with UOM and ${priceLabel}`;
};

/**
 * Catalog/custom SKU kinds only — not asset_component.
 * Pricing for components comes from UOM connection (see resolveAssetRentingPricingSku).
 */
export const isAssetRentingSkuRecordKind = (
  kind: unknown,
  skuTypeCodes?: Iterable<string> | null,
): boolean => {
  const value = String(kind || '').trim();
  if (!value) return false;
  if (value.toLowerCase() === 'sku') return true;
  const codes = new Set(
    [...(skuTypeCodes || [])].map((code) => String(code || '').trim().toUpperCase()).filter(Boolean),
  );
  return codes.has(value.toUpperCase());
};

/**
 * Pricing row for a rented item — prefers a row with a UOM selling price
 * (SKU, asset component with unit, or custom sku-tier), then related children / assetComponentId.
 */
export const resolveAssetRentingPricingSku = <T extends {
  id?: string
  kind?: string
  parentId?: string | null
  fields?: Record<string, unknown> | null
}>(
  assetRecord: T | null | undefined,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): T | null => {
  if (!assetRecord?.id) return null;
  const hasPrice = (row: T) => recordHasAssetRentingUomPrice(row, records);
  // Selected row already has UOM price (SKU or component with unit).
  if (hasPrice(assetRecord)) return assetRecord;

  // Asset → linked component field(s).
  for (const linkedId of resolveAssetComponentIds(assetRecord.fields)) {
    const linked = records.find((row) => String(row.id || '').trim() === linkedId);
    if (linked && hasPrice(linked)) return linked;
  }

  const assetId = String(assetRecord.id).trim();
  const isSkuKind = (row: T) => isAssetRentingSkuRecordKind(row.kind, skuTypeCodes);

  const directPriced = records.find(
    (row) => String(row.parentId || '').trim() === assetId && hasPrice(row),
  );
  if (directPriced) return directPriced;

  // BFS under the asset for any descendant with a UOM selling price.
  const queue = records
    .filter((row) => String(row.parentId || '').trim() === assetId)
    .map((row) => String(row.id || '').trim())
    .filter(Boolean);
  const seen = new Set<string>([assetId]);
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = records.find((row) => String(row.id || '').trim() === id);
    if (!node) continue;
    if (hasPrice(node)) return node;
    records.forEach((row) => {
      if (String(row.parentId || '').trim() === id) {
        const childId = String(row.id || '').trim();
        if (childId && !seen.has(childId)) queue.push(childId);
      }
    });
  }

  // Fallback: linked / child catalog SKU even without price yet (UI can prompt for UOM).
  for (const linkedId of resolveAssetComponentIds(assetRecord.fields)) {
    const linked = records.find((row) => String(row.id || '').trim() === linkedId);
    if (linked && isSkuKind(linked)) return linked;
  }
  const directSku = records.find(
    (row) => String(row.parentId || '').trim() === assetId && isSkuKind(row),
  );
  if (directSku) return directSku;

  const skuSearch = records
    .filter((row) => String(row.parentId || '').trim() === assetId)
    .map((row) => String(row.id || '').trim())
    .filter(Boolean);
  const skuSeen = new Set<string>([assetId]);
  while (skuSearch.length) {
    const id = skuSearch.shift()!;
    if (skuSeen.has(id)) continue;
    skuSeen.add(id);
    const node = records.find((row) => String(row.id || '').trim() === id);
    if (!node) continue;
    if (isSkuKind(node)) return node;
    records.forEach((row) => {
      if (String(row.parentId || '').trim() === id) {
        const childId = String(row.id || '').trim();
        if (childId && !skuSeen.has(childId)) skuSearch.push(childId);
      }
    });
  }

  return null;
};

/**
 * Work-order link pricing — priced UOM on the linked row (or connected SKU/component).
 * Room-registry / ROOM_TYPE_LINK fallbacks removed with hospitality.
 */
export const resolveAssetRentingWorkOrderLinkPricingRow = <T extends AssetRentingRecordLike>(
  linkRecord: T | null | undefined,
  records: T[],
  skuTypeCodes?: Iterable<string> | null,
): T | null => {
  if (!linkRecord) return null;
  const direct = resolveAssetRentingPricingSku(linkRecord, records, skuTypeCodes);
  if (direct && recordHasAssetRentingUomPrice(direct, records)) return direct;
  return null;
};

export const assetRentingWorkOrderLinkHasPricedRoom = (
  linkRecord: AssetRentingRecordLike | null | undefined,
  records: AssetRentingRecordLike[],
  skuTypeCodes?: Iterable<string> | null,
): boolean => Boolean(
  resolveAssetRentingWorkOrderLinkPricingRow(linkRecord, records, skuTypeCodes),
);

/** User-facing validation for work-order custom data links that need priced assets. */
export const assetRentingWorkOrderLinkPricingError = (
  linkRecord: AssetRentingRecordLike | null | undefined,
  records: AssetRentingRecordLike[],
  skuTypeCodes?: Iterable<string> | null,
): string | null => {
  if (!linkRecord?.id) return 'Pick a custom data item';
  if (assetRentingWorkOrderLinkHasPricedRoom(linkRecord, records, skuTypeCodes)) return null;
  return 'That item needs a UOM selling price (on the row or a connected SKU / component)';
};

export const parseWorkOrderAssetLink = (raw: unknown): WorkOrderAssetLink | null => {
  let src: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('{')) {
      try {
        src = JSON.parse(trimmed);
      } catch {
        return null;
      }
    } else {
      return null;
    }
  }
  if (!src || typeof src !== 'object' || Array.isArray(src)) return null;
  const row = src as Record<string, unknown>;
  const kind = String(row.kind || '').trim();
  const id = String(row.id || '').trim();
  if (!isWorkOrderAssetLinkKind(kind) || !id) return null;
  return { kind, id };
};

export const stringifyWorkOrderAssetLink = (link: WorkOrderAssetLink | null): string => {
  if (!link || !isWorkOrderAssetLinkKind(link.kind) || !String(link.id || '').trim()) return '';
  return JSON.stringify({
    kind: String(link.kind).trim().slice(0, 80),
    id: String(link.id).trim().slice(0, 80),
  });
};

/** Sale / expense / POS require one linked custom data item; basic and document clear the link. */
export const workOrderAssetLinkError = (
  workOrderKind: unknown,
  link: WorkOrderAssetLink | null | { kind?: unknown, id?: unknown },
): string | null => {
  const kind = normalizeWorkOrderKind(workOrderKind);
  if (!workOrderKindUsesDataLink(kind)) return null;
  const parsed = link && typeof link === 'object' && !Array.isArray(link)
    ? parseWorkOrderAssetLink({
      kind: (link as WorkOrderAssetLink).kind,
      id: (link as WorkOrderAssetLink).id,
    })
    : null;
  if (!parsed) {
    if (kind === 'sale') {
      return 'Sale work orders need one linked custom data item with UOM selling price (or a group with priced children)';
    }
    if (kind === 'expense') {
      return 'Expense work orders need one linked custom data item with UOM cost price (or a group with priced children)';
    }
    if (kind === 'pos_sales') {
      return 'POS sales work orders need one linked custom data catalog with UOM selling price (or children with UOM selling price)';
    }
  }
  return null;
};

/** Stamp UOM label onto asset fields for renting trackers — not for /logs Value (use track_transaction). */
export const applyAssetRentingUomPriceFields = (
  fields: Record<string, string>,
  price: AssetRentingUomPrice | null,
): Record<string, string> => {
  if (!price) return fields;
  return {
    ...fields,
    unit: price.uomName,
    uom: price.uomName,
  };
};

/** Desk booking — fields consumed by buildAssetRentingLogTrackingItems → track_transaction. */
export const buildAssetRentingBookingTransactionFields = (
  args: {
    rateAmount?: string
    customerRecordId?: string
    guestName?: string
    guestEmail?: string
    guestNumber?: string
    ratePlanId?: string
    ratePlanName?: string
    isCheckIn?: boolean
    assetCode?: string
  },
  uomPrice: AssetRentingUomPrice | null,
): Record<string, string> => {
  const price = String(args.rateAmount || uomPrice?.sellingPrice || '').trim();
  if (!price) return {};
  return {
    transactionPrice: price,
    rate: price,
    ...(uomPrice?.uomName ? { uom: uomPrice.uomName, unit: uomPrice.uomName } : {}),
    ...(args.customerRecordId ? {
      customerId: args.customerRecordId,
      guestCustomerId: args.customerRecordId,
    } : {}),
    ...(args.guestName ? { guestName: args.guestName, bookerName: args.guestName } : {}),
    ...(args.guestEmail ? { guestEmail: args.guestEmail, email: args.guestEmail } : {}),
    ...(args.guestNumber ? { guestNumber: args.guestNumber, number: args.guestNumber } : {}),
    ...(args.ratePlanId ? { ratePlanId: args.ratePlanId } : {}),
    ...(args.ratePlanName ? { ratePlan: args.ratePlanName, ratePlanName: args.ratePlanName } : {}),
    ...(args.assetCode ? { assetCode: args.assetCode } : {}),
    transactionType: args.isCheckIn ? 'check_in' : 'booking',
  };
};

export type ErpOperation = {
  id: string
  name: string
  /** Company code e.g. OP-001 — locked after create. */
  code: string
  scopeKind: OperationScopeKind
  scopeId: string
  workOrderId: string
  operationType: OperationType
  expiryMode: OperationExpiryMode
  expiresAt: string | null
  isSystem: boolean
  data: {
    description?: string
    triggerKeys?: string[]
    actionKey?: string
    /** Validated recipient when the linked work order requires an email object. */
    email?: string
    /** Designation record id when the linked work order requires a designation object. */
    designationId?: string
    /** Location record id when the linked work order requires a location object. */
    locationId?: string
    /** Department record id when the linked work order requires a department object. */
    departmentId?: string
    /** Team record id when the linked work order requires a team object. */
    teamId?: string
    /** Employee record id when the linked work order requires an employee object. */
    employeeId?: string
    /** Issuing manager when the linked work order uses general assign (set on create). */
    generalAssignIssuerId?: string
    /** Required object type(s) this operation must fill (JSON array string). */
    workOrderObjects?: string
    /** Multiline instructions when the work order opts into an object order field. */
    workOrderObjectOrder?: string
    /**
     * Per Yes/No work-order field side: one target record.
     * Assign → location / department / designation / team (not the assigned scope).
     * Reassign → peer of the work-order object scope under the same parent,
     *   or peer of the assigned scope when the work order has no such object.
     * Escalate → manager employee. Assign / Reassign always email everyone under the scope.
     */
    yesNoWorkflowTargets?: Record<string, {
      yes?: {
        recordKind: 'location' | 'department' | 'designation' | 'team' | 'employee'
        recordId: string
        notifyAllEmployeesInSpectrum?: boolean
      }
      no?: {
        recordKind: 'location' | 'department' | 'designation' | 'team' | 'employee'
        recordId: string
        notifyAllEmployeesInSpectrum?: boolean
      }
    }>
    /** roles = managers/supervisors/employees tagged by role; all_in_spectrum = everyone under the scope. */
    emailAudience?: 'roles' | 'all_in_spectrum'
    notifyAllEmployeesInSpectrum?: boolean
    /** Recurring cadence in milliseconds. */
    recurringIntervalMs?: number
    /** Daily HH:MM window — fixed_recurring keeps forms open inside this range. */
    fixedWindowStart?: string
    fixedWindowEnd?: string
    /** Next due time for recurring ops (ISO). */
    nextRunAt?: string
    /** Set when staff stops a recurring operation — blocks dispatch until cleared. */
    stoppedAt?: string
    /** Permanent revoke — closes open forms and blocks all future access/dispatch. */
    revokedAt?: string
    /** POS Added functionality — yes = Hospitality room folio charge at checkout. */
  }
  createdAt?: string
  updatedAt?: string
};

export const isOperationScopeKind = (raw: unknown): raw is OperationScopeKind => (
  OPERATION_SCOPE_KINDS.includes(raw as OperationScopeKind)
);

export const isOperationType = (raw: unknown): raw is OperationType => (
  OPERATION_TYPES.includes(raw as OperationType)
);

export const isOperationExpiryMode = (raw: unknown): raw is OperationExpiryMode => (
  OPERATION_EXPIRY_MODES.includes(raw as OperationExpiryMode)
);

export const isWorkOrderObjectType = (raw: unknown): raw is WorkOrderObjectType => (
  WORK_ORDER_OBJECT_TYPES.includes(raw as WorkOrderObjectType)
);

export const isWorkOrderObjectUserType = (raw: unknown): raw is WorkOrderObjectUserType => (
  WORK_ORDER_OBJECT_USER_TYPES.includes(raw as WorkOrderObjectUserType)
);

export const isValidOperationEmail = (email: string): boolean => (
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim())
);

export const operationNavGroupForScopeKind = (
  scopeKind: OperationScopeKind,
): OperationNavGroupLabel | null => {
  if (scopeKind === 'organization') return 'Directive';
  return 'All operations';
};

export const operationScopeKindForNavGroup = (
  group: OperationNavGroupLabel,
): OperationScopeKind | null => {
  if (group === 'Directive') return 'organization';
  return null;
};

/** Operations sidebar child groups — ERP work-order tier names. */
export const OPERATION_WORK_ORDER_GROUPS = [
  'Directives',
  'Mandates',
  'ManagerDirectives',
] as const;

export type OperationWorkOrderGroup = typeof OPERATION_WORK_ORDER_GROUPS[number];

export const OPERATION_WORK_ORDER_GROUP_LABELS: Record<OperationWorkOrderGroup, string> = {
  Directives: 'Directives',
  Mandates: 'CEO work orders',
  ManagerDirectives: 'Manager work orders',
};

/** Built-in organization triggers live under the Directive nav group (org master-data header). */
export const OPERATION_SYSTEM_WORK_ORDER_GROUP: OperationWorkOrderGroup = 'Directives';

type OperationLifecycleProbe = Pick<Partial<ErpOperation>, 'operationType' | 'expiryMode' | 'expiresAt' | 'data'>;

/** ISO timestamp when staff stopped a recurring operation. */
export const operationIsStopped = (op: OperationLifecycleProbe): boolean => {
  const raw = String(op.data?.stoppedAt || '').trim();
  if (!raw) return false;
  return !Number.isNaN(new Date(raw).getTime());
};

/** Revoke closes employee access — redispatch can restore it. */
export const operationIsRevoked = (op: OperationLifecycleProbe): boolean => {
  const raw = String(op.data?.revokedAt || '').trim();
  if (!raw) return false;
  return !Number.isNaN(new Date(raw).getTime());
};

/** Recurring / fixed-recurring operations can be stopped without deleting them. */
export const operationIsStoppable = (op: OperationLifecycleProbe): boolean => (
  op.operationType === 'recurring' || op.operationType === 'fixed_recurring'
);

/** Any non-built-in operation that is not already revoked can be revoked. */
export const operationIsRevokable = (
  op: OperationLifecycleProbe & { isSystem?: boolean },
): boolean => {
  if (op.isSystem) return false;
  return !operationIsRevoked(op);
};

export const operationIsActive = (op: OperationLifecycleProbe, now = Date.now()): boolean => {
  if (operationIsRevoked(op) || operationIsStopped(op)) return false;
  if (op.expiryMode === 'indefinite') return true;
  if (!op.expiresAt) return true;
  return new Date(op.expiresAt).getTime() > now;
};

/** Minimum lead time before a fixed expiry can take effect. */
export const OPERATION_EXPIRY_MIN_LEAD_MS = 2 * 60 * 1000;

/** Dev allows 1s recurring; otherwise lowest is 2 minutes. */
export const OPERATION_RECURRING_MIN_INTERVAL_MS_DEV = 1000;
export const OPERATION_RECURRING_MIN_INTERVAL_MS_PROD = 2 * 60 * 1000;

export const isOperationsDevMode = (): boolean => (
  !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
);

export const operationRecurringMinIntervalMs = (): number => (
  isOperationsDevMode()
    ? OPERATION_RECURRING_MIN_INTERVAL_MS_DEV
    : OPERATION_RECURRING_MIN_INTERVAL_MS_PROD
);

/** Earliest allowed datetime-local value — now plus minimum lead, local time. */
export const operationExpiryMinLocalDatetime = (now = new Date()): string => {
  const min = new Date(now.getTime() + OPERATION_EXPIRY_MIN_LEAD_MS);
  const y = min.getFullYear();
  const m = String(min.getMonth() + 1).padStart(2, '0');
  const d = String(min.getDate()).padStart(2, '0');
  const h = String(min.getHours()).padStart(2, '0');
  const mins = String(min.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${mins}`;
};

export const isOperationExpiryOnOrAfterToday = (
  expiresAt: string | null | undefined,
  now = new Date(),
): boolean => {
  const raw = String(expiresAt || '').trim();
  if (!raw) return false;
  const expiry = new Date(raw);
  if (Number.isNaN(expiry.getTime())) return false;
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const minExpiryMs = now.getTime() + OPERATION_EXPIRY_MIN_LEAD_MS;
  return expiry.getTime() >= todayStart.getTime() && expiry.getTime() >= minExpiryMs;
};

export const parseWorkOrderObjects = (raw: unknown): WorkOrderObjectType[] => {
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  const out: WorkOrderObjectType[] = [];
  parsed.forEach((item) => {
    const rawItem = String(item || '').trim();
    if ((REMOVED_WORK_ORDER_OBJECT_TYPES as readonly string[]).includes(rawItem)) return;
    const normalized: WorkOrderObjectType | '' = isWorkOrderObjectType(rawItem)
      ? rawItem as WorkOrderObjectType
      : '';
    if (normalized && !out.includes(normalized)) out.push(normalized);
  });
  return out;
};

export const stringifyWorkOrderObjects = (objects: WorkOrderObjectType[]): string => (
  JSON.stringify(objects)
);

/** Prefer operation.data.workOrderObjects; fall back to linked WO for legacy rows. */
export const resolveOperationWorkOrderObjects = (
  operationWorkOrderObjects: unknown,
  linkedWorkOrderObjects?: unknown,
): WorkOrderObjectType[] => {
  const fromOp = parseWorkOrderObjects(operationWorkOrderObjects);
  if (fromOp.length) return fromOp;
  return parseWorkOrderObjects(linkedWorkOrderObjects);
};

/** Operation objects allowed for an operation head scope (department+ cannot pick location object). */
export const operationObjectTypesForHeadScope = (
  scopeKind: string,
): WorkOrderObjectUserType[] => {
  const kind = String(scopeKind || '').trim();
  if (kind === 'department' || kind === 'designation' || kind === 'team') {
    return WORK_ORDER_OBJECT_USER_TYPES.filter((objectType) => objectType !== 'location');
  }
  return [...WORK_ORDER_OBJECT_USER_TYPES];
};

export const workOrderObjectsError = (
  objects: WorkOrderObjectType[],
  options?: { allowSystemEmail?: boolean, headScopeKind?: string },
): string | null => {
  if (!objects.length) return 'Pick at least one required operation object';
  if (objects.includes('location') && options?.headScopeKind) {
    const allowed = operationObjectTypesForHeadScope(options.headScopeKind);
    if (!allowed.includes('location')) {
      return 'Location operation object is not allowed for a department (or lower) head scope';
    }
  }
  // User ops cannot pick Email object — invite forms / system ops use allowSystemEmail.
  if (objects.includes('email') && !options?.allowSystemEmail) {
    return 'Email object is only for invite work orders — pick Designation, Location, Department, Team, Employee, or General assign object';
  }
  return null;
};

export const workOrderRequiresEmail = (rawObjects: unknown): boolean => (
  parseWorkOrderObjects(rawObjects).includes('email')
);

export const workOrderRequiresDesignation = (rawObjects: unknown): boolean => (
  parseWorkOrderObjects(rawObjects).includes('designation')
);

/** Employee object — any employee pick on linked operations. */
export const workOrderRequiresEmployee = (rawObjects: unknown): boolean => (
  parseWorkOrderObjects(rawObjects).includes('employee')
);

/** General assign — everyone under the issuing manager receives the operation. */
export const workOrderRequiresGeneralAssign = (rawObjects: unknown): boolean => (
  parseWorkOrderObjects(rawObjects).includes('general_assign')
);

/** Team object — team record pick on linked operations. */
export const workOrderRequiresTeam = (rawObjects: unknown): boolean => (
  parseWorkOrderObjects(rawObjects).includes('team')
);

/** Location object — location record pick on linked operations. */
export const workOrderRequiresLocation = (rawObjects: unknown): boolean => (
  parseWorkOrderObjects(rawObjects).includes('location')
);

/** Department object — department record pick on linked operations. */
export const workOrderRequiresDepartment = (rawObjects: unknown): boolean => (
  parseWorkOrderObjects(rawObjects).includes('department')
);

/** Max length for the optional object order on an operation. */
export const WORK_ORDER_OBJECT_ORDER_MAX = 4000;

/** Work order flag — when Yes, linked operations expose a multiline order field. */
export const workOrderIncludesObjectOrder = (raw: unknown): boolean => {
  const value = String(raw ?? '').trim().toLowerCase();
  return value === 'yes' || value === 'true' || value === '1';
};

/** Label for the operation order field from the work order's object type. */
export const workOrderObjectOrderLabel = (rawObjects: unknown): string => {
  const objects = parseWorkOrderObjects(rawObjects);
  if (objects.includes('designation')) return 'Designation object order';
  if (objects.includes('location')) return 'Location object order';
  if (objects.includes('department')) return 'Department object order';
  if (objects.includes('team')) return 'Team object order';
  if (objects.includes('employee')) return 'Employee object order';
  if (objects.includes('general_assign')) return 'General assign object order';
  if (objects.includes('email')) return 'Email object order';
  return 'Order';
};

export const normalizeWorkOrderObjectIncludeOrder = (raw: unknown): 'Yes' | 'No' => (
  workOrderIncludesObjectOrder(raw) ? 'Yes' : 'No'
);
